import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from '../../home/hooks/useInView';

const faqs = [
  {
    question: '什麼情況下需要申請專利或商標？',
    answer: '當您開發出新的技術、產品或服務，或是建立了獨特的品牌識別時，就應該考慮申請專利或商標。專利保護您的技術創新，商標保護您的品牌形象。建議在產品上市前或品牌推廣前就開始申請，以確保您的權益不被他人搶先註冊。',
  },
  {
    question: '初次諮詢通常會談些什麼？',
    answer: '初次諮詢時，我們會先了解您的業務性質、創新內容或品牌規劃，評估您目前的智產權狀況與潛在風險。接著會說明適合的保護方式、申請流程、所需時間與費用，並提供初步的策略建議。整個過程會用清楚易懂的方式說明，讓您充分了解後再做決定。',
  },
  {
    question: '新創公司什麼時候該開始處理智產權？',
    answer: '越早越好！許多新創公司在創業初期忽略智產權，等到產品成功後才發現已經無法申請專利，或是品牌被他人搶先註冊。建議在產品開發階段就開始規劃智產權策略，包括技術專利、品牌商標、營業秘密保護等，這不僅能保護您的創新成果，也能提升投資人的信心。',
  },
  {
    question: '是否提供跨國智產權諮詢？',
    answer: '是的，我們提供跨國智產權的諮詢與申請服務。如果您的產品或服務計劃進軍國際市場，我們會協助您規劃全球智產權布局，包括選擇適合的國家、申請策略與時程安排。我們與各國的專利商標事務所合作，能為您提供完整的跨國智產權保護。',
  },
  {
    question: '智產權申請需要多久時間？費用如何計算？',
    answer: '申請時間依類型而異：商標通常需要 8-12 個月，發明專利約 2-3 年。費用包含官方規費與服務費，會依據申請的複雜度、範圍與國家而有所不同。我們會在諮詢時提供詳細的時程與費用說明，讓您能充分評估與規劃預算。',
  },
];

export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [ref, isInView] = useInView({ threshold: 0.1 });

  return (
    <section className="py-24 bg-gradient-to-br from-[#f8f9fa] to-white">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="inline-block px-4 py-2 bg-[#2c5282]/10 rounded-full text-sm font-semibold text-[#2c5282] mb-4"
          >
            常見問題
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-4xl md:text-5xl font-bold text-[#1a365d] mb-6"
            style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
          >
            您可能想知道的問題
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-lg text-gray-600"
          >
            以下是客戶最常詢問的問題，希望能幫助您更了解我們的服務
          </motion.p>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:border-[#2c5282] transition-colors duration-300"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer hover:bg-gray-50 transition-colors"
              >
                <span className="text-lg font-semibold text-[#1a365d] pr-4" style={{ fontFamily: "'Noto Sans TC', sans-serif" }}>
                  {faq.question}
                </span>
                <motion.i
                  animate={{ rotate: openIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="ri-arrow-down-s-line text-2xl text-[#2c5282] flex-shrink-0"
                ></motion.i>
              </button>
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-5 text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}